import {ICartPosition, IMoney} from "@connox/typescript-library";
import {ICartMessage} from "@connox/typescript-library/types/cart/CartMessage";
import * as React from "react";
import {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import ApplicableVouchersModal from "../../lib/ModalWindow/ApplicableVouchersModal";
import {isCart, useCartContext, useUnsafeCartContext} from "../CartContextProvider/CartContextProvider";
import * as Sentry from "@sentry/browser";
import {IPercent} from "@connox/typescript-library/types/cart/Percent";
import {VoucherType} from "@connox/typescript-library/types/cart/VoucherType";
import styles from "./VoucherBlock.module.scss";
import {NO_VOUCHER_APPLICABLE_ITEM_FOUND, NOT_ALL_ITEMS_VOUCHER_APPLICABLE} from "../../../helpers/errorHandling";

export function escapeCode(code: string) {
  return encodeURIComponent(code.replace(/[äöüÄÖÜß!%&]/g, "?"));
}

const VoucherBlock: React.FC = () => {
  const context = useUnsafeCartContext();
  const {t} = useTranslation(["errorcodes", "translations"]);
  const voucherInputRef = useRef<HTMLInputElement>(null);
  // input value on init empty
  const [inputValue, setInputValue] = useState("");

  if (!context) {
    return null;
  }

  const SubmitVoucherLink = () => {
    // return nothing if input value is empty
    if (inputValue === "") {
      return null;
    }

    return (
      <span
        className={styles.redeemLink}
        onClick={(evt: React.MouseEvent<HTMLSpanElement>) => void submitVoucherCode(evt)}
      >
        {t("cart.redeem")}
      </span>
    );
  };

  const submitVoucherCode = async (evt: React.MouseEvent<HTMLSpanElement> | React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    if (!voucherInputRef.current?.value) {
      return;
    }

    const voucherCode = voucherInputRef.current.value;

    if (isCart(context)) {
      await context.cart.addVoucher(voucherCode);
      if (voucherInputRef.current) {
        setInputValue("");
      }
    } else {
      // todo finish this
      try {
        const {cart, done} = await context.requestCart();
        await cart.addVoucher(voucherCode);
        if (voucherInputRef.current) {
          setInputValue("");
        }
        done();
      } catch (e) {
        Sentry.captureException(e);
      }
    }
  };

  return (
    <div className={styles.voucher}>
      <form onSubmit={(evt: React.FormEvent<HTMLFormElement>) => void submitVoucherCode(evt)}>
        <input
          value={inputValue}
          id="CartVoucherInput"
          type="text"
          name="VoucherInput"
          onChange={(e) => setInputValue(e.target.value)}
          ref={voucherInputRef}
          placeholder={t("cart.redeemVoucher")}
        />
        <SubmitVoucherLink />
      </form>
      {isCart(context) && <VoucherList />}
    </div>
  );
};

const EmptyVoucher: React.FC<{position: ICartPosition}> = (props) => {
  if (!props.position.reference) {
    return null;
  }
  const {voucherCode} = props.position.reference;
  const {messages, text} = props.position;

  return (
    <VoucherLine
      code={voucherCode || ""}
      messages={messages}
      minimumOrderValueAbsolute={null}
      minimumOrderQuantityAbsolute={null}
      minimumOrderQuantityMissing={0}
      text={text}
      voucherAmount={null}
      voucherPercentage={null}
      type={null}
    />
  );
};

/*
 * whole voucher list
 */
const VoucherList: React.FC = () => {
  const {cart} = useCartContext();
  const voucherLines = cart.positions.filter((pos: ICartPosition) => pos.type === "Voucher");

  /*
   * Remove double elements from object
   */
  const voucherLinesReduced = voucherLines.reduce((previous: ICartPosition[], current) => {
    if (!previous.some((pos) => pos.reference?.voucherCode === current.reference?.voucherCode)) {
      return previous.concat(current);
    } else {
      return previous;
    }
  }, []);

  const {rebates} = cart;

  return (
    <div className={styles.list}>
      {rebates.map((rebate) => (
        <VoucherLine key={rebate.code} {...rebate} text={rebate.code} />
      ))}
      {voucherLinesReduced.map((voucherLine, i) => {
        const {reference, text} = voucherLine;

        if (!reference?.voucher) {
          return <EmptyVoucher key={`voucher_switch_${i}`} position={voucherLine} />;
        }

        return <VoucherLine key={reference.voucherCode} {...reference.voucher} text={text} />;
      })}
    </div>
  );
};

const VoucherLine: React.FC<{
  code: string;
  messages: ICartMessage[];
  minimumOrderValueAbsolute: IMoney | null;
  minimumOrderQuantityAbsolute: number | null;
  minimumOrderQuantityMissing: number | 0;
  text: string;
  type: VoucherType | null;
  voucherAmount: IMoney | null;
  voucherPercentage: IPercent | null;
}> = (props) => {
  const {cart} = useCartContext();
  const {
    code,
    messages,
    minimumOrderValueAbsolute,
    minimumOrderQuantityAbsolute,
    minimumOrderQuantityMissing,
    text,
    type,
    voucherAmount,
    voucherPercentage,
  } = props;
  const voucherValue = type === "Fix" ? voucherAmount : voucherPercentage;
  const voucherName = voucherValue?.formatted ? `${voucherValue.formatted} (${text})` : text;
  const {t} = useTranslation(["translations"]);

  return (
    <div key={`voucher_${code}`} className={styles.list__row}>
      <div>
        <div className={styles.voucherName}>
          {messages.length ? (
            <span className={`icon icon-exclamation--red ${styles.iconCautionOutline}`} />
          ) : (
            <span className={`icon icon-check--green ${styles.checkIcon}`} />
          )}
          <span>{voucherName}</span>
        </div>
        <div className={styles.voucherRemove}>
          <span
            className={styles.list__row__delete}
            onClick={() => {
              void cart.removeVoucher(escapeCode(code));
            }}
            style={{cursor: "pointer"}}
          >
            <span className="icon icon-trash--red" />
          </span>
        </div>
      </div>
      {minimumOrderValueAbsolute && minimumOrderValueAbsolute.amount !== "0.00" && (
        <div className={styles.list__row__text}>
          <div className={styles.voucherCaution} />
          <div className={styles.voucherWarning}>
            <span>{t("general.voucherExpirationMinValueReturn")}</span>
          </div>
        </div>
      )}
      {messages.map((msg) => (
        <VoucherItemMessage
          key={`${code}_${msg.code}`}
          currentVoucherCode={code}
          minimumOrderValue={minimumOrderValueAbsolute?.formatted || ""}
          minimumQuantity={minimumOrderQuantityAbsolute}
          minimumQuantityMissing={minimumOrderQuantityMissing}
          msg={msg}
          voucherTitle={text}
          voucherType={type}
        />
      ))}
    </div>
  );
};

const VoucherItemMessage: React.FC<{
  currentVoucherCode?: string;
  minimumOrderValue: string;
  minimumQuantity: number | null;
  minimumQuantityMissing: number;
  msg: ICartMessage;
  voucherTitle: string;
  voucherType: VoucherType | null;
}> = (prop) => {
  const {
    currentVoucherCode,
    minimumOrderValue,
    minimumQuantity,
    minimumQuantityMissing,
    msg,
    voucherTitle,
    voucherType,
  } = prop;
  const {t} = useTranslation(["errorcodes", "translations"]);
  const [popupVisible, setPopupVisible] = useState<boolean>(false);

  const message = t(msg.code, {
    count: minimumQuantityMissing,
    minimumOrderValue,
    minimumQuantity,
    minimumQuantityMissing,
    voucherCode: currentVoucherCode,
  });

  const WhyNotLink: React.FC = () =>
    msg.code === NOT_ALL_ITEMS_VOUCHER_APPLICABLE || msg.code === NO_VOUCHER_APPLICABLE_ITEM_FOUND ? (
      <React.Fragment>
        {". "}
        <a
          href="#"
          onClick={(evt: React.MouseEvent<HTMLAnchorElement>) => {
            evt.preventDefault();
            setPopupVisible(true);
          }}
        >
          {t("general.whyNot")}
        </a>
      </React.Fragment>
    ) : null;

  return (
    <div key={msg.code} className={styles.list__row__text}>
      <div className={styles.voucherCaution} />
      <div className={styles.voucherWarning}>
        <ApplicableVouchersModal
          closeFunction={() => setPopupVisible(false)}
          isVisible={popupVisible}
          voucherTitle={voucherTitle}
          voucherType={voucherType}
        />
        <b>{message}</b>
        <WhyNotLink />
      </div>
    </div>
  );
};

/**
 * @component
 */
export default VoucherBlock;
